<template>
  <div v-if="events?.length && allEventsPath" class="sidebar-block">
    <div class="detail-layout-events">
      <div class="flex justify-between">
        <h2 class="title is-h5">
          {{ $texts('upcomingEvents') }}
        </h2>
        <SpriteSymbol name="calendar" class="size-6" />
      </div>
      <div class="events-list">
        <NuxtLink
          v-for="event in events"
          :key="event.id"
          class="events-list-item"
          :to="`${allEventsPath}?eventId=${event.guidleId}`"
        >
          <div class="title is-size-6">
            {{ event.name }}
          </div>
          <ul class="info-list is-size-7">
            <li>{{ event.nextOccurrence?.formatted }}</li>
          </ul>
          <div
            v-if="sameAreaPois && sameAreaPois.length && event.location"
            class="is-size-7"
          >
            {{ event.location?.name }}
          </div>
        </NuxtLink>
      </div>
      <div class="detail-layout-events-button">
        <NuxtLink :to="allEventsPath" class="link with-arrow">
          {{ $texts('showAllEvents') }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { NodePlaceFragment } from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import { TZDate } from '@date-fns/tz'
import { parse, isAfter } from 'date-fns'

const { $texts } = useEasyTexts()

const props = defineProps<{
  nid: string
  sameAreaPois?: NodePlaceFragment['sameAreaPois']
}>()

const data = useInitData()
const language = useCurrentLanguage()

const allEventsPath = computed(() => data.value.pages.eventCalendar)

const { data: events } = await useAsyncGraphqlQuery(
  'loadDiscoverSwissEvents',
  { id: props.nid },
  {
    transform: function (data) {
      const entity = data.data.entityById
      const dateFormat = 'dd.MM.yyyy - HH:mm'
      const today = new TZDate(new Date(), 'Europe/Zurich')
      if (
        entity &&
        'discoverSwissItem' in entity &&
        entity.discoverSwissItem?.eventsQuery?.items
      ) {
        return (entity.discoverSwissItem.eventsQuery.items || [])
          .filter(notNullish)
          .filter((event) => {
            if (event.nextOccurrence?.formatted) {
              const parsed = parse(
                event.nextOccurrence.formatted,
                dateFormat,
                today,
              )
              return isAfter(parsed, today)
            }
          })
          .sort((a, b) => {
            if (a.nextOccurrence?.formatted && b.nextOccurrence?.formatted) {
              return (
                parse(a.nextOccurrence.formatted, dateFormat, today).getTime() -
                parse(b.nextOccurrence.formatted, dateFormat, today).getTime()
              )
            }
            return 0
          })
      }

      return []
    },
    deep: false,
    graphqlCaching: {
      client: true,
    },
    watch: [language],
  },
)
</script>

<style>
@reference '~/assets/css/tailwind.css';
.detail-layout-events {
  @apply relative;
  > .icon {
    @apply absolute top-0 right-0 text-size-4-5 text-scheme-invert;
  }
}
.events-list-item {
  @apply block border-b border-solid border-border px-0 py-[0.75rem];
  .title {
    @apply mb-[0.25em];
  }
  &:hover {
    @apply bg-scheme-main-bis text-black;
  }
  .info-list {
    @apply text-scheme-invert-ter;
  }
}
.info-list {
  @apply flex leading-none;
  > li {
    &:not(:last-child) {
      &:after {
        content: ' - ';
        @apply mx-[0.5em] my-0 inline-block;
      }
    }
  }
}
.detail-layout-events-button {
  @apply mt-[1rem];
}
</style>
